import { useMemo, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { Plus, Archive, RotateCcw, Trash2, Lock } from "lucide-react";
import { api, formatApiError, PROJECT_LIFECYCLE_LABELS } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function ProjectSettingsPage() {
  const { projectId, project, members, reload } = useOutletContext();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [partSel, setPartSel] = useState("");
  const [partRole, setPartRole] = useState("member");
  const isAdminMgr = !!project.can_admin;
  const isEI = project.workspace_type === "interdepartmental";

  const participantOptions = useMemo(() => {
    const taken = new Set((project.participants || []).map((p) => p.user_id));
    return members.filter((m) => !taken.has(m.id));
  }, [members, project]);

  const addParticipant = async () => {
    if (!partSel) return;
    try { await api.post(`/projects/${projectId}/participants`, { user_id: partSel, project_role: partRole }); toast.success("Participante adicionado."); setPartSel(""); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const removeParticipant = async (uid) => {
    try { await api.delete(`/projects/${projectId}/participants/${uid}`); toast.success("Participante removido."); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const archive = async () => { try { await api.post(`/projects/${projectId}/archive`); toast.success("Projeto arquivado."); reload(); } catch (e) { toast.error(formatApiError(e)); } };
  const setLifecycle = async (val) => { try { await api.patch(`/projects/${projectId}`, { project_status: val }); toast.success("Status atualizado."); reload(); } catch (e) { toast.error(formatApiError(e)); } };
  const restore = async () => { try { await api.post(`/projects/${projectId}/restore`); toast.success("Projeto restaurado."); reload(); } catch (e) { toast.error(formatApiError(e)); } };
  const remove = async () => {
    if (!window.confirm("Excluir definitivamente este projeto?")) return;
    try { await api.delete(`/projects/${projectId}`); toast.success("Projeto excluído."); navigate("/projetos"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="project-settings-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{project.workspace_name}</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-2 flex items-center gap-2">
            Configurações
            {project.confidential && <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md bg-rose-50 text-rose-700"><Lock className="w-3 h-3" /> Confidencial</span>}
          </h1>
          <p className="text-slate-500 mt-1 max-w-2xl">{project.description || "Sem descrição."}</p>
        </div>
        {isAdminMgr && (
          <div className="flex items-center gap-2">
            {project.status === "active"
              ? <button onClick={archive} data-testid="archive-project-btn" className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-200 text-sm hover:bg-slate-50"><Archive className="w-4 h-4" /> Arquivar</button>
              : <button onClick={restore} data-testid="restore-project-btn" className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-200 text-sm hover:bg-slate-50"><RotateCcw className="w-4 h-4" /> Restaurar</button>}
            {user?.role === "admin" && (
              <button onClick={remove} data-testid="delete-project-btn" className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-red-200 text-sm text-red-700 hover:bg-red-50"><Trash2 className="w-4 h-4" /> Excluir</button>
            )}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="project-status-section">
        <h2 className="font-heading text-lg font-semibold mb-3">Status do projeto</h2>
        {isAdminMgr ? (
          <select value={["planning","in_progress","paused"].includes(project.project_status) ? project.project_status : "in_progress"} onChange={(e) => setLifecycle(e.target.value)} data-testid="project-status-select"
            className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm">
            {[["planning","Planejamento"],["in_progress","Em andamento"],["paused","Pausado"]].map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        ) : (
          <span className="text-sm text-slate-700">{PROJECT_LIFECYCLE_LABELS[project.project_status] || "Em andamento"}</span>
        )}
        <p className="text-xs text-slate-400 mt-2">O status é independente do arquivamento. Para concluir o projeto, use "Concluir projeto" na Visão geral.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="project-participants">
        <h2 className="font-heading text-lg font-semibold mb-3">Participantes ({project.participants.length})</h2>
        <div className="flex flex-wrap gap-2 mb-3">
          {project.participants.length === 0 && <span className="text-sm text-slate-400">Nenhum participante.</span>}
          {project.participants.map((p) => (
            <span key={p.user_id} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-slate-100 text-slate-700" data-testid={`participant-${p.user_id}`}>
              {p.name || p.user_id} · {p.project_role === "viewer" ? "Visualizador" : "Membro"}
              {isAdminMgr && <button onClick={() => removeParticipant(p.user_id)} className="hover:text-red-600" data-testid={`remove-participant-${p.user_id}`}>×</button>}
            </span>
          ))}
        </div>
        {isAdminMgr && (
          <div className="flex items-center gap-2 flex-wrap">
            <select value={partSel} onChange={(e) => setPartSel(e.target.value)} data-testid="participant-select" className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm">
              <option value="">{isEI ? "Selecionar gerente do E.I..." : "Selecionar do departamento..."}</option>
              {participantOptions.map((m) => <option key={m.id} value={m.id}>{m.name} — {m.email}</option>)}
            </select>
            {!isEI && (
              <select value={partRole} onChange={(e) => setPartRole(e.target.value)} data-testid="participant-role" className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm">
                <option value="member">Membro</option>
                <option value="viewer">Visualizador</option>
              </select>
            )}
            <button onClick={addParticipant} disabled={!partSel} data-testid="add-participant-btn" className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-slate-900 text-white text-sm disabled:opacity-50"><Plus className="w-4 h-4" /> Adicionar</button>
          </div>
        )}
      </div>
    </div>
  );
}
